<template>
    <v-container fluid class="pa-4">
        <v-card class="mb-3">
            <v-card-title class="px-3 py-2 flex-wrap" style="row-gap:8px">
                <v-icon left small color="primary">mdi-sitemap</v-icon>
                <span class="text-subtitle-1 font-weight-medium">Organigramma</span>
                <v-spacer></v-spacer>
                <v-text-field
                    v-model="search"
                    placeholder="Cerca dipendente o reparto"
                    clearable
                    hide-details
                    dense
                    outlined
                    single-line
                    style="max-width:280px"
                    class="mr-2 org-search">
                    <template v-slot:prepend-inner>
                        <v-icon small>mdi-magnify</v-icon>
                    </template>
                </v-text-field>
                <v-btn-toggle v-model="view" mandatory dense>
                    <v-btn small value="tree" text>
                        <v-icon left small>mdi-file-tree</v-icon>
                        Albero
                    </v-btn>
                    <v-btn small value="chart" text>
                        <v-icon left small>mdi-graph</v-icon>
                        Grafico
                    </v-btn>
                </v-btn-toggle>
                <v-btn icon class="ml-2" @click="load" :loading="loading"><v-icon>mdi-refresh</v-icon></v-btn>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text v-if="!loading">
                <div v-if="totals" class="text-caption grey--text mb-2">
                    {{ totals.departments }} reparti · {{ totals.employees }} dipendenti
                    <span v-if="totals.orphans"> · <strong>{{ totals.orphans }} senza reparto</strong></span>
                </div>

                <!-- Vista albero -->
                <div v-if="view === 'tree' && filteredTree.length">
                    <v-treeview
                        :items="treeItems"
                        :open="openIds"
                        item-key="key"
                        item-children="children"
                        item-text="name"
                        open-on-click
                        dense
                        hoverable>
                        <template v-slot:prepend="{ item }">
                            <v-icon small :color="item.kind === 'dept' ? 'primary' : (item.kind === 'manager' ? 'orange' : 'grey')">
                                {{ item.kind === 'dept' ? 'mdi-domain' : (item.kind === 'manager' ? 'mdi-account-tie' : 'mdi-account') }}
                            </v-icon>
                        </template>
                        <template v-slot:label="{ item }">
                            <span :class="item.kind === 'dept' ? 'font-weight-bold' : ''">{{ item.name }}</span>
                            <span v-if="item.subtitle" class="text-caption grey--text ml-2">{{ item.subtitle }}</span>
                        </template>
                        <template v-slot:append="{ item }">
                            <v-btn v-if="item.employee_id" icon x-small :to="'/hremployees/' + item.employee_id">
                                <v-icon x-small>mdi-open-in-new</v-icon>
                            </v-btn>
                        </template>
                    </v-treeview>
                </div>

                <!-- Vista grafico -->
                <div v-else-if="view === 'chart' && filteredTree.length" class="orgchart-wrapper">
                    <ul class="orgchart">
                        <li v-for="root in filteredTree" :key="root.id">
                            <org-node :node="root" :on-select="openDetail"></org-node>
                        </li>
                    </ul>
                </div>

                <v-alert v-if="!tree.length" type="info" dense text>
                    Nessun reparto configurato. Aggiungi reparti da Impostazioni HR → Reparti.
                </v-alert>
                <v-alert v-else-if="searchQuery && !hasSearchHits" type="warning" dense text>
                    Nessun risultato per "<strong>{{ search }}</strong>".
                </v-alert>

                <v-dialog v-model="detailDialog" max-width="560" scrollable>
                    <v-card v-if="selectedDept">
                        <v-card-title class="px-4 py-2">
                            <v-icon left small color="primary">mdi-domain</v-icon>
                            <span class="text-subtitle-1 font-weight-medium">{{ selectedDept.name }}</span>
                            <span v-if="selectedDept.code" class="text-caption grey--text ml-2">{{ selectedDept.code }}</span>
                            <v-spacer></v-spacer>
                            <v-btn icon small @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text class="pt-3" style="max-height:60vh">
                            <div v-if="selectedDept.manager_name" class="mb-3">
                                <div class="text-caption grey--text mb-1">Manager</div>
                                <v-chip small :to="'/hremployees/' + selectedDept.manager_id" outlined>
                                    <v-icon left small color="orange">mdi-account-tie</v-icon>
                                    {{ selectedDept.manager_name }}
                                </v-chip>
                            </div>
                            <div v-if="selectedDept.employees && selectedDept.employees.length">
                                <div class="text-caption grey--text mb-1">Dipendenti ({{ selectedDept.employees.length }})</div>
                                <v-list dense class="pa-0">
                                    <v-list-item
                                        v-for="emp in selectedDept.employees"
                                        :key="emp.id"
                                        :to="'/hremployees/' + emp.id"
                                        @click="detailDialog = false">
                                        <v-list-item-icon class="my-2 mr-3">
                                            <v-icon small :color="emp.id === selectedDept.manager_id ? 'orange' : 'grey'">
                                                {{ emp.id === selectedDept.manager_id ? 'mdi-account-tie' : 'mdi-account' }}
                                            </v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-content>
                                            <v-list-item-title>{{ emp.first_name }} {{ emp.last_name }}</v-list-item-title>
                                            <v-list-item-subtitle v-if="emp.job_title">{{ emp.job_title }}</v-list-item-subtitle>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list>
                            </div>
                            <div v-else class="grey--text text-caption">
                                Nessun dipendente assegnato a questo reparto.
                            </div>
                        </v-card-text>
                    </v-card>
                </v-dialog>

                <v-card v-if="filteredOrphans && filteredOrphans.length" outlined class="mt-4">
                    <v-card-title class="px-3 py-2 text-subtitle-2">
                        <v-icon left small color="warning">mdi-alert-circle</v-icon>
                        Dipendenti senza reparto ({{ filteredOrphans.length }}<span v-if="searchQuery && filteredOrphans.length !== orphans.length"> di {{ orphans.length }}</span>)
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="d-flex flex-wrap" style="gap:6px">
                        <v-chip v-for="o in filteredOrphans" :key="o.id" small :to="'/hremployees/' + o.id" outlined>
                            <v-icon left small>mdi-account</v-icon>
                            {{ o.first_name }} {{ o.last_name }}
                        </v-chip>
                    </v-card-text>
                </v-card>
            </v-card-text>
        </v-card>
    </v-container>
</template>

<script>
const OrgNode = {
    name: 'org-node',
    props: { node: Object, onSelect: Function },
    template: `
        <div class="org-subtree">
            <div class="org-node">
                <div class="org-card org-dept org-card-clickable" @click="onSelect && onSelect(node)" title="Vedi dipendenti">
                    <div class="org-card-icon"><i class="mdi mdi-domain" style="color:#fff;font-size:16px"></i></div>
                    <div class="org-card-name">{{ node.name }}</div>
                    <div v-if="node.code" class="org-card-code">{{ node.code }}</div>
                    <div v-if="node.manager_name" class="org-card-sub">
                        <i class="mdi mdi-account-tie" style="font-size:13px"></i>
                        {{ node.manager_name }}
                    </div>
                    <div v-if="node.employees && node.employees.length" class="org-card-emp">
                        <i class="mdi mdi-account-multiple" style="font-size:13px"></i>
                        {{ node.employees.length }} dipendenti
                        <i class="mdi mdi-chevron-right org-card-chevron"></i>
                    </div>
                </div>
            </div>
            <ul v-if="(node.children && node.children.length)">
                <li v-for="child in node.children" :key="child.id">
                    <org-node :node="child" :on-select="onSelect"></org-node>
                </li>
            </ul>
        </div>
    `,
}

export default {
    name: 'hr-org-chart',
    components: { OrgNode },

    data() {
        return {
            view: 'tree',
            tree: [],
            orphans: [],
            totals: null,
            loading: false,
            detailDialog: false,
            selectedDept: null,
            search: '',
        }
    },

    computed: {
        searchQuery() {
            return (this.search || '').toLowerCase().trim()
        },
        filteredTree() {
            const q = this.searchQuery
            if (!q) return this.tree
            const matchEmp = (e) => (
                ((e.first_name || '') + ' ' + (e.last_name || '')).toLowerCase().includes(q) ||
                (e.job_title || '').toLowerCase().includes(q)
            )
            const filterNode = (n) => {
                const deptMatch = (n.name || '').toLowerCase().includes(q) ||
                                  (n.code || '').toLowerCase().includes(q) ||
                                  (n.manager_name || '').toLowerCase().includes(q)
                const matchedEmployees = (n.employees || []).filter(matchEmp)
                const filteredChildren = (n.children || []).map(filterNode).filter(Boolean)
                if (!deptMatch && !matchedEmployees.length && !filteredChildren.length) return null
                return {
                    ...n,
                    manager_id: deptMatch ? n.manager_id : null,
                    manager_name: deptMatch ? n.manager_name : null,
                    employees: deptMatch ? (n.employees || []) : matchedEmployees,
                    children: filteredChildren,
                }
            }
            return this.tree.map(filterNode).filter(Boolean)
        },
        filteredOrphans() {
            const q = this.searchQuery
            if (!q) return this.orphans
            return this.orphans.filter(e => (
                ((e.first_name || '') + ' ' + (e.last_name || '')).toLowerCase().includes(q) ||
                (e.job_title || '').toLowerCase().includes(q)
            ))
        },
        hasSearchHits() {
            return this.filteredTree.length > 0 || this.filteredOrphans.length > 0
        },
        openIds() {
            // quando c'è una ricerca, espandi tutti i reparti filtrati per mostrare i match
            if (this.searchQuery) {
                const ids = []
                const walk = (nodes) => {
                    nodes.forEach(n => {
                        ids.push('d:' + n.id)
                        if (n.children) walk(n.children)
                    })
                }
                walk(this.filteredTree)
                return ids
            }
            return this.filteredTree.map(r => 'd:' + r.id)
        },
        treeItems() {
            // Trasforma la struttura backend in items per v-treeview
            const map = (n) => {
                const children = []
                if (n.manager_id) {
                    children.push({
                        key: 'mgr:' + n.manager_id + ':' + n.id,
                        name: n.manager_name,
                        kind: 'manager',
                        subtitle: 'manager reparto',
                        employee_id: n.manager_id,
                    })
                }
                ;(n.employees || []).forEach(e => {
                    if (e.id === n.manager_id) return // evita duplicato
                    children.push({
                        key: 'emp:' + e.id + ':' + n.id,
                        name: (e.first_name + ' ' + e.last_name).trim(),
                        kind: 'employee',
                        subtitle: e.job_title || '',
                        employee_id: e.id,
                    })
                })
                ;(n.children || []).forEach(c => children.push(map(c)))
                return {
                    key: 'd:' + n.id,
                    name: n.name,
                    kind: 'dept',
                    subtitle: n.code || '',
                    children,
                }
            }
            return this.filteredTree.map(map)
        },
    },

    methods: {
        load() {
            this.loading = true
            this.axios.get('hrdepartments/tree').then(r => {
                this.tree = r.data.tree || []
                this.orphans = r.data.orphans || []
                this.totals = r.data.totals || null
                this.loading = false
            }, () => { this.loading = false })
        },
        openDetail(node) {
            // mostra sempre il reparto completo, anche se la ricerca aveva filtrato manager/dipendenti
            this.selectedDept = this.findDeptById(node.id, this.tree) || node
            this.detailDialog = true
        },
        findDeptById(id, nodes) {
            for (const n of nodes || []) {
                if (n.id === id) return n
                const found = this.findDeptById(id, n.children)
                if (found) return found
            }
            return null
        },
    },

    created() {
        this.$store.commit('endProgress')
        this.load()
    },
}
</script>

<style>
/* Org chart classico (orizzontale via flex, linee CSS) */
.orgchart-wrapper {
    overflow-x: auto;
    overflow-y: hidden;
    padding: 12px 0;
    -webkit-overflow-scrolling: touch;
}
.orgchart, .orgchart ul {
    list-style: none;
    padding-top: 16px;
    position: relative;
    margin: 0;
    transition: all 0.5s;
}
.orgchart {
    text-align: center;
    display: flex;
    justify-content: flex-start;
    width: max-content;
    min-width: 100%;
    margin: 0 auto;
}
.orgchart ul {
    display: flex;
    justify-content: center;
}
.orgchart li {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    list-style: none;
    position: relative;
    padding: 16px 6px 0 6px;
}
/* connettore verticale al genitore */
.orgchart li::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    border-left: 1px solid #bbb;
    height: 16px;
}
/* connettori orizzontali tra fratelli */
.orgchart li::after {
    content: '';
    position: absolute;
    top: 0;
    height: 1px;
    background: #bbb;
    left: 0;
    right: 0;
}
.orgchart li:first-child::after {
    left: 50%;
}
.orgchart li:last-child::after {
    right: 50%;
}
.orgchart li:only-child::after {
    display: none;
}
.orgchart > li::before, .orgchart > li::after {
    display: none;
}
.org-subtree {
    display: flex;
    flex-direction: column;
    align-items: center;
}
.org-card {
    background: white;
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 8px 12px;
    min-width: 160px;
    max-width: 220px;
    box-shadow: 0 1px 3px rgba(0,0,0,.06);
    text-align: left;
    position: relative;
    margin: 0 4px;
}
.org-card.org-dept {
    border-top: 3px solid #1976d2;
}
.org-card-clickable {
    cursor: pointer;
    transition: box-shadow .2s ease, transform .1s ease, border-color .2s ease;
}
.org-card-clickable:hover {
    box-shadow: 0 4px 10px rgba(0,0,0,.12);
    transform: translateY(-1px);
    border-color: #1976d2;
}
.org-card-chevron {
    float: right;
    font-size: 14px;
    opacity: .5;
}
.org-search.v-text-field--outlined.v-input--dense .v-input__prepend-inner {
    margin-top: 0;
    align-self: center;
    padding-top: 0;
}
.org-search.v-text-field--outlined.v-input--dense .v-input__append-inner {
    margin-top: 0;
    align-self: center;
    padding-top: 0;
}
.org-card-icon {
    background: #1976d2;
    width: 24px; height: 24px;
    border-radius: 4px;
    display: inline-flex; align-items: center; justify-content: center;
    margin-bottom: 4px;
}
.org-card-name {
    font-weight: 600;
    font-size: 13px;
    line-height: 1.2;
    margin-bottom: 4px;
}
.org-card-sub, .org-card-emp {
    font-size: 11px;
    color: #666;
}
.org-card-code {
    font-size: 10px;
    color: #999;
    text-transform: uppercase;
    letter-spacing: .5px;
    margin-bottom: 4px;
}
.theme--dark .org-card {
    background: #1e1e1e;
    border-color: #444;
}
</style>
